import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { Input, Button, Box, Text, Icon } from '@deseretbook/react-ui'
import URI from 'urijs'

import { register } from '../../api/users'
import { libraryUrl } from '../../utils/routes'

/**
 * Generic registration form. Displays input fields and handles registration and redirecting
 * the user to the proper place.
 */
class RegistrationForm extends Component {
  /**
   * Component constructor.
   */
  constructor() {
    super()

    this.state = {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
      },
      errorMessage: '',
      isLoading: false,
    }

    this.onSubmitHandler = this.onSubmitHandler.bind(this)
    this.onChangeHandler = this.onChangeHandler.bind(this)
    this.formIsValid = this.formIsValid.bind(this)
    this.validatePassword = this.validatePassword.bind(this)
  }

  /**
   * Validates the password to make sure it has the following:
   * - At least one lowercase letter
   * - At least one uppercase letter
   * - At least one number
   * - At least one special character (.,*#-)
   *
   * @param {string} password - Password to validate
   * @returns {object} Object with booleans for each validation
   */
  validatePassword(password) {
    return {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password),
    }
  }

  /**
   * Makes sure the form is submitted, then registers the user. If there's a `next`
   * route param, then the user is redirected there, otherwise they are redirected
   * to the homepage.
   *
   * @param {event} e - submit event
   */
  onSubmitHandler(e) {
    e.preventDefault()

    if (this.formIsValid()) {
      const { history } = this.props

      this.setState({ isLoading: true })

      register(this.state.form)
        .then(() => {
          this.setState({ isLoading: false })

          if (history) {
            const routeParams = URI().search(true)

            history.push(
              'next' in routeParams ? decodeURIComponent(routeParams.next) : libraryUrl(),
            )
          }
        })
        .catch((error) => {
          this.setState({ errorMessage: error.message, isLoading: false })
        })
    }
  }

  /**
   * Handles change of form value.
   *
   * @param {object} e - event
   */
  onChangeHandler(e) {
    const { name, value } = e.target

    this.setState({
      form: {
        ...this.state.form,
        [name]: value,
      },
    })
  }

  /**
   * Checks to see if the form is valid.
   *
   * @returns {boolean} If form is valid or not
   */
  formIsValid() {
    const { email, password, passwordConfirmation } = this.state.form
    const { hasLowercase, hasUppercase, hasNumber, hasSpecial } = this.validatePassword(password)

    // Ensure that all password criteria are met
    return (
      email &&
      password &&
      passwordConfirmation &&
      password === passwordConfirmation &&
      hasLowercase &&
      hasUppercase &&
      hasNumber &&
      hasSpecial
    )
  }

  /**
   * Determines how to render the component.
   *
   * @returns {function} Component
   */
  render() {
    const { errorMessage, isLoading } = this.state
    const { firstName, lastName, email, password, passwordConfirmation } = this.state.form

    // Get password validation results to dynamically update icons
    const { hasLowercase, hasUppercase, hasNumber, hasSpecial } = this.validatePassword(password)
    const hasMixedCase = hasLowercase && hasUppercase

    return (
      <form onSubmit={this.onSubmitHandler}>
        <Box alignItems="center" justifyContent="space-between" className="form-input-name-wrapper">
          <Input
            name="firstName"
            label="First Name"
            type="text"
            value={firstName}
            disabled={isLoading}
            onChange={this.onChangeHandler}
            inputWrapperProps={{ className: 'form-input-name-container' }}
            className="form-input-name"
            removeHint
          />
          <Input
            name="lastName"
            label="Last Name"
            type="text"
            value={lastName}
            disabled={isLoading}
            onChange={this.onChangeHandler}
            inputWrapperProps={{ className: 'form-input-name-container' }}
            className="form-input-name"
            removeHint
          />
        </Box>
        <Input
          name="email"
          label="Deseret Book email"
          type="email"
          required
          value={email}
          disabled={isLoading}
          onChange={this.onChangeHandler}
          inputWrapperProps={{ className: 'form-input-container' }}
          removeHint
        />
        <Input
          name="password"
          label="Password"
          type="password"
          required
          value={password}
          disabled={isLoading}
          onChange={this.onChangeHandler}
          inputWrapperProps={{ className: 'form-input-container' }}
          removeHint
        />
        <Box className="form-password-helper" style={{ marginBottom: '16px' }}>
          <Box alignItems="center" style={{ marginBottom: '4px' }}>
            <Icon
              className="icon"
              name={hasMixedCase ? 'check' : 'close'}
              color={hasMixedCase ? 'success' : 'danger'}
            />
            <Text className="text">Lower and uppercase letters</Text>
          </Box>
          <Box alignItems="center" style={{ marginBottom: '4px' }}>
            <Icon
              className="icon"
              name={hasNumber ? 'check' : 'close'}
              color={hasNumber ? 'success' : 'danger'}
            />
            <Text className="text">At least one number</Text>
          </Box>
          <Box alignItems="center">
            <Icon
              className="icon"
              name={hasSpecial ? 'check' : 'close'}
              color={hasSpecial ? 'success' : 'danger'}
            />
            <Text className="text">At least on special character (.,*#-)</Text>
          </Box>
        </Box>
        <Input
          name="passwordConfirmation"
          label="Password Confirmation"
          type="password"
          required
          value={passwordConfirmation}
          disabled={isLoading}
          onChange={this.onChangeHandler}
          inputWrapperProps={{ className: 'form-input-container' }}
          removeHint
          pattern={password}
        />
        {!!errorMessage && <div>{errorMessage}</div>}
        <Button
          type="submit"
          disabled={!this.formIsValid() || isLoading}
          isLoading={isLoading}
          className="submit-button"
        >
          Sign Up
        </Button>
        {this.props.children}
      </form>
    )
  }
}

RegistrationForm.propTypes = {
  /** window history object */
  history: PropTypes.shape({}).isRequired,
  children: PropTypes.node,
}

RegistrationForm.defaultProps = {
  children: false,
}

export default withRouter(RegistrationForm)
